(() => {
  'use strict';

  const KG_PER_LB = 0.45359237;
  const DAYS_PER_MONTH = 30.4375;

  const MINIMUM_SEX_DIFFERENCE_RATIO = 0.08;
  const FEMALE_WEIGHT_MULTIPLIER = 0.94;
  const MALE_WEIGHT_MULTIPLIER = 1.06;

  const FOOD_DEFAULTS = {
    puppy: {
      cup: 375,
      kg: 3700
    },
    kitten: {
      cup: 450,
      kg: 4100
    }
  };

  const SIZE_PRESETS = {
    puppy: [
      {
        value: '',
        label: 'Or pick by size (for mixes and unknowns)'
      },
      {
        value: '7',
        label: 'Small (about 7 kg / 15 lb)'
      },
      {
        value: '18',
        label: 'Medium (about 18 kg / 40 lb)'
      },
      {
        value: '32',
        label: 'Large (about 32 kg / 70 lb)'
      },
      {
        value: '55',
        label: 'Giant (about 55 kg / 120 lb)'
      }
    ],
    kitten: [
      {
        value: '',
        label: 'Or pick by size (for moggies and unknowns)'
      },
      {
        value: '3.5',
        label: 'Small cat (about 3.5 kg / 8 lb)'
      },
      {
        value: '4.5',
        label: 'Average cat (about 4.5 kg / 10 lb)'
      },
      {
        value: '6.5',
        label: 'Large cat (about 6.5 kg / 14 lb)'
      }
    ]
  };

  class SFeedingCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.state = {
        species: this.getInitialSpecies(),
        selectedBreed: null,
        selectedSex: '',
        selectedSizeWeightKg: null,
        calculated: false,
        adultWeightWasAutoFilled: false,
        suppressAdultWeightInputEvent: false,
        previousWeightUnit: 'kg',
        previousAdultWeightUnit: 'kg',
        breeds: {
          puppy: [],
          kitten: []
        },
        breedsLoaded: {
          puppy: false,
          kitten: false
        }
      };

      this.cacheElements();
      this.bindEvents();
      this.initialise();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    cacheElements() {
      this.form = this.querySelector('[data-pkfc-form]');
      this.submitButton = this.querySelector('[data-pkfc-submit]');

      this.speciesInputs = this.querySelectorAll('[name="species"]');
      this.weightUnitInputs = this.querySelectorAll(
        '[name="weightUnit"]'
      );
      this.ageUnitInputs = this.querySelectorAll('[name="ageUnit"]');
      this.adultWeightUnitInputs = this.querySelectorAll(
        '[name="adultWeightUnit"]'
      );
      this.foodUnitInputs = this.querySelectorAll('[name="foodUnit"]');
      this.sexInputs = this.querySelectorAll('[name="sex"]');

      this.weightInput = this.querySelector('[data-pkfc-weight]');
      this.ageInput = this.querySelector('[data-pkfc-age]');
      this.adultWeightInput = this.querySelector(
        '[data-pkfc-adult-weight]'
      );
      this.foodCaloriesInput = this.querySelector(
        '[data-pkfc-food-calories]'
      );

      this.breedHint = this.querySelector('[data-pkfc-breed-hint]');
      this.breedCombo = this.querySelector(
        '[data-pkfc-breed-combobox]'
      );
      this.breedInput = this.querySelector('[data-pkfc-breed-input]');
      this.breedPanel = this.querySelector('[data-pkfc-breed-panel]');

      this.breedEstimate = this.querySelector(
        '[data-pkfc-breed-estimate]'
      );
      this.breedEstimateValue = this.querySelector(
        '[data-pkfc-breed-estimate-value]'
      );
      this.breedEstimateNote = this.querySelector(
        '[data-pkfc-breed-estimate-note]'
      );
      this.breedNote = this.querySelector('[data-pkfc-breed-note]');

      this.sexRow = this.querySelector('[data-pkfc-sex-row]');
      this.sizePreset = this.querySelector('[data-pkfc-size-preset]');

      this.errorsWrap = this.querySelector('[data-pkfc-errors]');

      this.results = this.querySelector('[data-pkfc-results]');
      this.whoLine = this.querySelector('[data-pkfc-who-line]');
      this.weaningBanner = this.querySelector(
        '[data-pkfc-weaning-banner]'
      );
      this.solidResults = this.querySelector(
        '[data-pkfc-solid-results]'
      );

      this.kcalValue = this.querySelector('[data-pkfc-kcal]');
      this.bowlAmount = this.querySelector('[data-pkfc-bowl-amount]');
      this.bowlCaption = this.querySelector(
        '[data-pkfc-bowl-caption]'
      );

      this.mealsWrap = this.querySelector('[data-pkfc-meals]');
      this.mealReason = this.querySelector(
        '[data-pkfc-meal-reason]'
      );

      this.largeBreedWarning = this.querySelector(
        '[data-pkfc-large-breed-warning]'
      );
      this.largeBreedHeading = this.querySelector(
        '[data-pkfc-large-breed-heading]'
      );
      this.largeBreedText = this.querySelector(
        '[data-pkfc-large-breed-text]'
      );

      this.trajectory = this.querySelector('[data-pkfc-trajectory]');
      this.milkStage = this.querySelector('[data-pkfc-milk-stage]');

      this.ctaCopy = this.querySelector('[data-pkfc-cta-copy]');
      this.enrichment = this.querySelector('[data-pkfc-enrichment]');
      this.saveButton = this.querySelector('[data-pkfc-save]');
      this.primaryCta = this.querySelector('[data-pkfc-primary-cta]');
      this.restartButton = this.querySelector('[data-pkfc-restart]');
    }

    bindEvents() {
      if (!this.signal) return;

      this.form?.addEventListener(
        'submit',
        (event) => this.onSubmit(event),
        {
          signal: this.signal
        }
      );

      this.speciesInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onSpeciesChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.weightUnitInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onWeightUnitChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.ageUnitInputs.forEach((input) => {
        input.addEventListener(
          'change',
          () => this.recalculateIfReady(),
          {
            signal: this.signal
          }
        );
      });

      this.adultWeightUnitInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onAdultWeightUnitChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.foodUnitInputs.forEach((input) => {
        input.addEventListener(
          'change',
          () => this.recalculateIfReady(),
          {
            signal: this.signal
          }
        );
      });

      this.sexInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onSexChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.breedInput?.addEventListener(
        'focus',
        () => this.openBreedPanel(),
        {
          signal: this.signal
        }
      );

      this.breedInput?.addEventListener(
        'input',
        () => this.onBreedSearchInput(),
        {
          signal: this.signal
        }
      );

      this.breedInput?.addEventListener(
        'keydown',
        (event) => this.onBreedInputKeydown(event),
        {
          signal: this.signal
        }
      );

      this.breedPanel?.addEventListener(
        'mousedown',
        (event) => this.onBreedPanelMouseDown(event),
        {
          signal: this.signal
        }
      );

      this.sizePreset?.addEventListener(
        'change',
        () => this.onSizePresetChange(),
        {
          signal: this.signal
        }
      );

      this.weightInput?.addEventListener(
        'input',
        () => this.recalculateIfReady(),
        {
          signal: this.signal
        }
      );

      this.ageInput?.addEventListener(
        'input',
        () => this.recalculateIfReady(),
        {
          signal: this.signal
        }
      );

      this.adultWeightInput?.addEventListener(
        'input',
        () => this.onAdultWeightInput(),
        {
          signal: this.signal
        }
      );

      this.foodCaloriesInput?.addEventListener(
        'input',
        () => this.recalculateIfReady(),
        {
          signal: this.signal
        }
      );

      this.restartButton?.addEventListener(
        'click',
        () => this.restart(),
        {
          signal: this.signal
        }
      );

      this.saveButton?.addEventListener(
        'click',
        () => this.handleSaveButton(),
        {
          signal: this.signal
        }
      );

      document.addEventListener(
        'click',
        (event) => this.onDocumentClick(event),
        {
          signal: this.signal
        }
      );

      document.addEventListener(
        'keydown',
        (event) => this.onDocumentKeydown(event),
        {
          signal: this.signal
        }
      );
    }

    async initialise() {
      this.setCheckedSpecies(this.state.species);

      this.state.previousWeightUnit = this.getSelectedValue(
        'weightUnit',
        'kg'
      );

      this.state.previousAdultWeightUnit = this.getSelectedValue(
        'adultWeightUnit',
        'kg'
      );

      this.applyStaticLinks();
      this.updateSpeciesInterface();
      this.clearErrors();
      this.hideResults();

      await this.loadBreedData();
    }

    applyStaticLinks() {
      const primaryCtaLink = String(
        this.dataset.primaryCtaLink || ''
      ).trim();

      if (primaryCtaLink && this.primaryCta) {
        this.primaryCta.href = primaryCtaLink;
      }
    }

    async loadBreedData() {
      const dogUrl = String(
        this.dataset.dogBreedsUrl || ''
      ).trim();

      const catUrl = String(
        this.dataset.catBreedsUrl || ''
      ).trim();

      await Promise.allSettled([
        this.loadSpeciesBreedData('puppy', dogUrl),
        this.loadSpeciesBreedData('kitten', catUrl)
      ]);

      if (document.activeElement === this.breedInput) {
        this.renderBreedPanel(this.breedInput?.value || '');
      }
    }

    async loadSpeciesBreedData(species, url) {
      if (!url) {
        console.warn(
          `[Feeding Calculator] Missing breed JSON URL for ${species}.`
        );

        return;
      }

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${response.statusText}`
          );
        }

        const rawData = await response.json();

        const breeds = this.extractBreedArray(rawData)
          .map((record) => this.normaliseBreedRecord(record))
          .filter(Boolean)
          .sort((first, second) =>
            first.name.localeCompare(second.name)
          );

        this.state.breeds[species] = breeds;
        this.state.breedsLoaded[species] = true;
      } catch (error) {
        console.error(
          `[Feeding Calculator] Could not load ${species} breeds:`,
          error
        );

        this.state.breeds[species] = [];
        this.state.breedsLoaded[species] = false;
      }
    }

    extractBreedArray(rawData) {
      if (Array.isArray(rawData)) {
        return rawData;
      }

      if (Array.isArray(rawData?.breeds)) {
        return rawData.breeds;
      }

      if (Array.isArray(rawData?.data)) {
        return rawData.data;
      }

      if (
        rawData?.breeds &&
        typeof rawData.breeds === 'object'
      ) {
        return Object.values(rawData.breeds);
      }

      return [];
    }

    normaliseBreedRecord(record) {
      if (!record || typeof record !== 'object') {
        return null;
      }

      const name = String(
        record.name ||
        record.breed ||
        record.label ||
        ''
      ).trim();

      if (!name) {
        return null;
      }

      const adultWeight =
        record.adultWeightKg || {};

      const sexWeights =
        record.sexSpecificAdultWeightKg ||
        record.sexSpecificWeightsKg ||
        record.adultWeightBySexKg ||
        {};

      const overallMinKg = this.toNullablePositiveNumber(
        adultWeight.min ??
        adultWeight.minKg
      );

      const overallMaxKg = this.toNullablePositiveNumber(
        adultWeight.max ??
        adultWeight.maxKg
      );

      const female = this.normaliseSexWeight(
        sexWeights.female
      );

      const male = this.normaliseSexWeight(
        sexWeights.male
      );

      const overallTypicalKg =
        this.toNullablePositiveNumber(
          adultWeight.typical ??
          adultWeight.typicalKg
        ) ||
        this.toNullablePositiveNumber(
          record.calculator?.defaultWeightKg
        ) ||
        this.midpoint(
          female.typicalKg,
          male.typicalKg
        ) ||
        this.midpoint(
          overallMinKg,
          overallMaxKg
        );

      if (
        !overallTypicalKg &&
        !female.typicalKg &&
        !male.typicalKg
      ) {
        return null;
      }

      return {
        id: String(
          record.id ||
          record.slug ||
          this.slugify(name)
        ),
        name,
        size: String(
          record.size ||
          record.sizeCategory ||
          ''
        ).trim(),
        adultWeightKg: {
          min: overallMinKg,
          max: overallMaxKg,
          typical: overallTypicalKg
        },
        sexSpecificAdultWeightKg: {
          female,
          male
        },
        calculator: {
          defaultWeightKg:
            this.toNullablePositiveNumber(
              record.calculator?.defaultWeightKg
            ) ||
            overallTypicalKg,
          minimumReasonableWeightKg:
            this.toNullablePositiveNumber(
              record.calculator?.minimumReasonableWeightKg
            ) ||
            overallMinKg,
          maximumReasonableWeightKg:
            this.toNullablePositiveNumber(
              record.calculator?.maximumReasonableWeightKg
            ) ||
            overallMaxKg
        },
        dataQuality: {
          confidence: this.normaliseConfidence(
            record.dataQuality?.confidence
          ),
          weightSourceType: String(
            record.dataQuality?.weightSourceType || ''
          ).trim(),
          sexSpecificValuesAreEstimated:
            record.dataQuality
              ?.sexSpecificValuesAreEstimated !== false,
          notes: String(
            record.dataQuality?.notes || ''
          ).trim()
        }
      };
    }

    normaliseSexWeight(record) {
      if (!record || typeof record !== 'object') {
        return {
          minKg: null,
          maxKg: null,
          typicalKg: null
        };
      }

      const minKg = this.toNullablePositiveNumber(
        record.minKg ??
        record.min
      );

      const maxKg = this.toNullablePositiveNumber(
        record.maxKg ??
        record.max
      );

      const typicalKg =
        this.toNullablePositiveNumber(
          record.typicalKg ??
          record.typical ??
          record.defaultKg ??
          record.weightKg
        ) ||
        this.midpoint(minKg, maxKg);

      return {
        minKg,
        maxKg,
        typicalKg
      };
    }

    normaliseConfidence(value) {
      const confidence = String(value || '')
        .trim()
        .toLowerCase();

      if (
        confidence === 'high' ||
        confidence === 'medium' ||
        confidence === 'low'
      ) {
        return confidence;
      }

      return 'medium';
    }

    getInitialSpecies() {
      const configuredSpecies = String(
        this.dataset.defaultSpecies || ''
      ).toLowerCase();

      if (
        configuredSpecies === 'puppy' ||
        configuredSpecies === 'kitten'
      ) {
        return configuredSpecies;
      }

      return (
        this.querySelector('[name="species"]:checked')?.value ||
        'puppy'
      );
    }

    setCheckedSpecies(species) {
      this.speciesInputs.forEach((input) => {
        input.checked = input.value === species;
      });
    }

    getSelectedValue(name, fallback = '') {
      return (
        this.querySelector(`[name="${name}"]:checked`)?.value ||
        fallback
      );
    }

    currentBreedList() {
      return this.state.breeds[this.state.species] || [];
    }

    onSpeciesChange(event) {
      const species = String(
        event.target?.value || ''
      );

      if (
        species !== 'puppy' &&
        species !== 'kitten'
      ) {
        return;
      }

      this.state.species = species;

      this.clearBreedSelection({
        clearBreedInput: true,
        clearAdultWeight: true
      });

      this.state.selectedSizeWeightKg = null;

      this.updateSpeciesInterface();
      this.clearErrors();
      this.hideResults();
    }

    updateSpeciesInterface() {
      const isKitten =
        this.state.species === 'kitten';

      if (this.breedHint) {
        this.breedHint.textContent = isKitten
          ? String(
              this.dataset.kittenBreedHint ||
              'Most cats end up between 3.5 and 6 kg. Pick the breed and we estimate, or leave it and we assume an average cat.'
            )
          : String(
              this.dataset.puppyBreedHint ||
              'Most owners know the breed but not the adult weight. Pick it and we do the maths. Not purebred? Choose “Mixed breed or not sure”.'
            );
      }

      if (this.breedInput) {
        this.breedInput.placeholder = isKitten
          ? String(
              this.dataset.kittenBreedPlaceholder ||
              'Search, e.g. Ragdoll, Maine Coon, Domestic Shorthair'
            )
          : String(
              this.dataset.puppyBreedPlaceholder ||
              'Search, e.g. Labrador, Cavoodle, Kelpie'
            );
      }

      if (this.adultWeightInput) {
        this.adultWeightInput.placeholder = isKitten
          ? String(
              this.dataset.kittenAdultPlaceholder ||
              'e.g. 4.5'
            )
          : String(
              this.dataset.puppyAdultPlaceholder ||
              'e.g. 30'
            );
      }

      this.renderSizePresets();
      this.closeBreedPanel();
      this.hideBreedEstimate();
      this.hideBreedNote();

      if (this.sexRow) {
        this.sexRow.hidden = true;
      }
    }

    renderSizePresets() {
      if (!this.sizePreset) {
        return;
      }

      const presets =
        SIZE_PRESETS[this.state.species] ||
        SIZE_PRESETS.puppy;

      this.sizePreset.innerHTML = presets
        .map(
          (preset) => `
            <option value="${this.escapeHtml(preset.value)}">
              ${this.escapeHtml(preset.label)}
            </option>
          `
        )
        .join('');
    }

    onBreedSearchInput() {
      const enteredValue = String(
        this.breedInput?.value || ''
      );

      if (
        this.state.selectedBreed &&
        enteredValue !== this.state.selectedBreed.name
      ) {
        this.clearBreedSelection({
          clearBreedInput: false,
          clearAdultWeight:
            this.state.adultWeightWasAutoFilled
        });
      }

      this.renderBreedPanel(enteredValue);
      this.openBreedPanel();
    }

    openBreedPanel() {
      if (!this.breedPanel || !this.breedInput) {
        return;
      }

      this.renderBreedPanel(this.breedInput.value);
      this.breedPanel.hidden = false;

      this.breedInput.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    closeBreedPanel() {
      if (!this.breedPanel || !this.breedInput) {
        return;
      }

      this.breedPanel.hidden = true;

      this.breedInput.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    renderBreedPanel(filter = '') {
      if (!this.breedPanel) {
        return;
      }

      const query = this.normaliseSearchText(filter);
      const breeds = this.currentBreedList();

      if (!this.state.breedsLoaded[this.state.species]) {
        this.breedPanel.innerHTML = `
          <div class="feeding-calculator__breed-empty">
            Breed data is loading. You can still choose a size or enter
            the expected adult weight manually.
          </div>
        `;

        return;
      }

      const matches = query
        ? breeds.filter((breed) =>
            this.normaliseSearchText(breed.name).includes(query)
          )
        : breeds;

      const mixedLabel =
        this.state.species === 'kitten'
          ? 'Moggie / mixed / not sure'
          : 'Mixed breed or not sure';

      const items = [
        `
          <div
            class="feeding-calculator__breed-option feeding-calculator__breed-option--mixed"
            data-pkfc-breed-mixed
            role="option"
            aria-selected="false"
          >
            <span class="feeding-calculator__breed-option-name">
              ${this.escapeHtml(mixedLabel)}
            </span>
          </div>
        `
      ];

      matches.forEach((breed) => {
        items.push(`
          <div
            class="feeding-calculator__breed-option"
            data-pkfc-breed-id="${this.escapeHtml(breed.id)}"
            role="option"
            aria-selected="false"
          >
            <span class="feeding-calculator__breed-option-name">
              ${this.escapeHtml(breed.name)}
            </span>

            <span class="feeding-calculator__breed-option-weight">
              ${this.escapeHtml(
                this.formatBreedListWeight(breed)
              )}
            </span>
          </div>
        `);
      });

      if (!matches.length) {
        items.push(`
          <div class="feeding-calculator__breed-empty">
            No breed match found. Choose “${this.escapeHtml(
              mixedLabel
            )}”, pick a size, or enter the expected adult weight manually.
          </div>
        `);
      }

      this.breedPanel.innerHTML = items.join('');
    }

    formatBreedListWeight(breed) {
      const minimum = this.toNullablePositiveNumber(
        breed.adultWeightKg?.min
      );

      const maximum = this.toNullablePositiveNumber(
        breed.adultWeightKg?.max
      );

      const typical =
        this.toNullablePositiveNumber(
          breed.calculator?.defaultWeightKg
        ) ||
        this.toNullablePositiveNumber(
          breed.adultWeightKg?.typical
        );

      if (
        minimum &&
        maximum &&
        Math.abs(maximum - minimum) >= 0.05
      ) {
        return `~${this.formatDecimal(
          minimum,
          1
        )}–${this.formatDecimal(
          maximum,
          1
        )} kg`;
      }

      if (typical) {
        return `~${this.formatDecimal(
          typical,
          1
        )} kg`;
      }

      return '';
    }

    onBreedPanelMouseDown(event) {
      const mixedOption = event.target.closest(
        '[data-pkfc-breed-mixed]'
      );

      if (mixedOption) {
        event.preventDefault();
        this.selectMixedBreed();
        this.closeBreedPanel();
        return;
      }

      const option = event.target.closest(
        '[data-pkfc-breed-id]'
      );

      if (!option) {
        return;
      }

      event.preventDefault();

      const breedId = String(
        option.dataset.pkfcBreedId || ''
      );

      const breed = this.currentBreedList().find(
        (item) => item.id === breedId
      );

      if (!breed) {
        return;
      }

      this.selectBreed(breed);
      this.closeBreedPanel();
    }

    onBreedInputKeydown(event) {
      if (event.key === 'Escape') {
        this.closeBreedPanel();
        return;
      }

      if (
        event.key !== 'Enter' ||
        this.breedPanel?.hidden
      ) {
        return;
      }

      const firstOption = this.breedPanel?.querySelector(
        '[data-pkfc-breed-mixed], [data-pkfc-breed-id]'
      );

      if (!firstOption) {
        return;
      }

      event.preventDefault();

      firstOption.dispatchEvent(
        new MouseEvent('mousedown', {
          bubbles: true
        })
      );
    }

    selectBreed(breed) {
      this.state.selectedBreed = breed;
      this.state.selectedSex = 'female';
      this.state.selectedSizeWeightKg = null;

      if (this.breedInput) {
        this.breedInput.value = breed.name;
      }

      if (this.sizePreset) {
        this.sizePreset.value = '';
      }

      this.setSelectedSex('female');

      if (this.sexRow) {
        this.sexRow.hidden = false;
      }

      this.applySelectedEstimateToInput();
      this.renderBreedEstimate();
      this.renderBreedNote();
      this.recalculateIfReady();
    }

    setSelectedSex(sex) {
      this.state.selectedSex = sex;

      this.sexInputs.forEach((input) => {
        input.checked = input.value === sex;
      });
    }

    selectMixedBreed() {
      const label =
        this.state.species === 'kitten'
          ? 'Moggie / mixed / not sure'
          : 'Mixed breed or not sure';

      this.clearBreedSelection({
        clearBreedInput: false,
        clearAdultWeight:
          this.state.adultWeightWasAutoFilled
      });

      this.state.selectedSizeWeightKg = null;

      if (this.breedInput) {
        this.breedInput.value = label;
      }

      if (this.sizePreset) {
        this.sizePreset.value = '';
      }

      this.showBreedNote(
        'Choose the closest size below, or enter the expected adult weight if you know it.',
        'medium'
      );

      this.recalculateIfReady();
    }

    clearBreedSelection({
      clearBreedInput = true,
      clearAdultWeight = false
    } = {}) {
      this.state.selectedBreed = null;
      this.state.selectedSex = '';

      if (clearBreedInput && this.breedInput) {
        this.breedInput.value = '';
      }

      this.resetSexSelection();

      if (this.sexRow) {
        this.sexRow.hidden = true;
      }

      this.hideBreedEstimate();
      this.hideBreedNote();

      if (
        clearAdultWeight &&
        this.adultWeightInput
      ) {
        this.setAdultWeightInputValue('');
        this.state.adultWeightWasAutoFilled = false;
      }
    }

    onSexChange(event) {
      const sex = String(
        event.target?.value || ''
      );

      if (
        sex !== 'female' &&
        sex !== 'male'
      ) {
        return;
      }

      if (!this.state.selectedBreed) {
        return;
      }

      this.setSelectedSex(sex);
      this.applySelectedEstimateToInput();
      this.renderBreedEstimate();
      this.renderBreedNote();
      this.recalculateIfReady();
    }

    resetSexSelection() {
      this.sexInputs.forEach((input) => {
        input.checked = false;
      });
    }

    onSizePresetChange() {
      const selectedValue = Number(
        this.sizePreset?.value || 0
      );

      if (
        !Number.isFinite(selectedValue) ||
        selectedValue <= 0
      ) {
        this.state.selectedSizeWeightKg = null;

        if (!this.state.selectedBreed) {
          this.hideBreedEstimate();
        }

        return;
      }

      this.clearBreedSelection({
        clearBreedInput: true,
        clearAdultWeight:
          this.state.adultWeightWasAutoFilled
      });

      this.state.selectedSizeWeightKg = selectedValue;

      const mixedLabel =
        this.state.species === 'kitten'
          ? 'Moggie / mixed / not sure'
          : 'Mixed breed or not sure';

      if (this.breedInput) {
        this.breedInput.value = mixedLabel;
      }

      this.applySelectedEstimateToInput();
      this.renderBreedEstimate();

      this.showBreedNote(
        'This is a general size-based estimate. Edit the expected adult weight if you have a better figure.',
        'medium'
      );

      this.recalculateIfReady();
    }

    getSelectedEstimate() {
      if (this.state.selectedBreed) {
        return this.getBreedEstimate(
          this.state.selectedBreed,
          this.state.selectedSex
        );
      }

      if (this.state.selectedSizeWeightKg) {
        return {
          typicalKg: this.state.selectedSizeWeightKg,
          minKg: this.state.selectedSizeWeightKg,
          maxKg: this.state.selectedSizeWeightKg,
          femaleKg: null,
          maleKg: null,
          source: 'size'
        };
      }

      return null;
    }

    getEffectiveSexWeights(breed) {
      if (!breed) {
        return {
          femaleKg: null,
          maleKg: null
        };
      }

      const overallTypical =
        this.toNullablePositiveNumber(
          breed.calculator?.defaultWeightKg
        ) ||
        this.toNullablePositiveNumber(
          breed.adultWeightKg?.typical
        ) ||
        this.midpoint(
          breed.adultWeightKg?.min,
          breed.adultWeightKg?.max
        );

      const storedFemaleKg =
        this.toNullablePositiveNumber(
          breed.sexSpecificAdultWeightKg
            ?.female?.typicalKg
        ) ||
        this.midpoint(
          breed.sexSpecificAdultWeightKg
            ?.female?.minKg,
          breed.sexSpecificAdultWeightKg
            ?.female?.maxKg
        );

      const storedMaleKg =
        this.toNullablePositiveNumber(
          breed.sexSpecificAdultWeightKg
            ?.male?.typicalKg
        ) ||
        this.midpoint(
          breed.sexSpecificAdultWeightKg
            ?.male?.minKg,
          breed.sexSpecificAdultWeightKg
            ?.male?.maxKg
        );

      if (!overallTypical) {
        return {
          femaleKg: storedFemaleKg,
          maleKg: storedMaleKg
        };
      }

      const storedAverage =
        storedFemaleKg && storedMaleKg
          ? (storedFemaleKg + storedMaleKg) / 2
          : null;

      const storedDifferenceRatio =
        storedAverage
          ? Math.abs(
              storedMaleKg - storedFemaleKg
            ) / storedAverage
          : 0;

      if (
        storedFemaleKg &&
        storedMaleKg &&
        storedMaleKg > storedFemaleKg &&
        storedDifferenceRatio >=
          MINIMUM_SEX_DIFFERENCE_RATIO
      ) {
        return {
          femaleKg: storedFemaleKg,
          maleKg: storedMaleKg
        };
      }

      return {
        femaleKg:
          overallTypical *
          FEMALE_WEIGHT_MULTIPLIER,
        maleKg:
          overallTypical *
          MALE_WEIGHT_MULTIPLIER
      };
    }

    getBreedEstimate(breed, sex = '') {
      if (!breed) {
        return null;
      }

      const effectiveSexWeights =
        this.getEffectiveSexWeights(breed);

      const femaleKg =
        effectiveSexWeights.femaleKg;

      const maleKg =
        effectiveSexWeights.maleKg;

      const overallTypical =
        this.toNullablePositiveNumber(
          breed.calculator?.defaultWeightKg
        ) ||
        this.toNullablePositiveNumber(
          breed.adultWeightKg?.typical
        ) ||
        this.midpoint(femaleKg, maleKg) ||
        this.midpoint(
          breed.adultWeightKg?.min,
          breed.adultWeightKg?.max
        );

      let typicalKg = overallTypical;
      let source = 'breed';

      if (sex === 'female' && femaleKg) {
        typicalKg = femaleKg;
        source = 'breed_and_sex';
      }

      if (sex === 'male' && maleKg) {
        typicalKg = maleKg;
        source = 'breed_and_sex';
      }

      if (!typicalKg) {
        return null;
      }

      const selectedSexRecord =
        sex === 'female'
          ? breed.sexSpecificAdultWeightKg?.female
          : sex === 'male'
            ? breed.sexSpecificAdultWeightKg?.male
            : null;

      return {
        typicalKg,
        minKg:
          this.toNullablePositiveNumber(
            selectedSexRecord?.minKg
          ) ||
          this.toNullablePositiveNumber(
            breed.adultWeightKg?.min
          ) ||
          typicalKg,
        maxKg:
          this.toNullablePositiveNumber(
            selectedSexRecord?.maxKg
          ) ||
          this.toNullablePositiveNumber(
            breed.adultWeightKg?.max
          ) ||
          typicalKg,
        femaleKg,
        maleKg,
        source
      };
    }

    applySelectedEstimateToInput() {
      if (!this.adultWeightInput) {
        return;
      }

      const estimate = this.getSelectedEstimate();

      if (!estimate?.typicalKg) {
        return;
      }

      const unit = this.getSelectedValue(
        'adultWeightUnit',
        'kg'
      );

      const value =
        unit === 'lb'
          ? estimate.typicalKg / KG_PER_LB
          : estimate.typicalKg;

      this.setAdultWeightInputValue(
        this.formatInputNumber(value)
      );

      this.state.adultWeightWasAutoFilled = true;
    }

    setAdultWeightInputValue(value) {
      if (!this.adultWeightInput) {
        return;
      }

      this.state.suppressAdultWeightInputEvent = true;
      this.adultWeightInput.value = value;
      this.state.suppressAdultWeightInputEvent = false;
    }

    onAdultWeightInput() {
      if (!this.state.suppressAdultWeightInputEvent) {
        this.state.adultWeightWasAutoFilled = false;
      }

      this.recalculateIfReady();
    }

    renderBreedEstimate() {
      if (
        !this.breedEstimate ||
        !this.breedEstimateValue ||
        !this.breedEstimateNote
      ) {
        return;
      }

      const estimate = this.getSelectedEstimate();

      if (!estimate) {
        this.hideBreedEstimate();
        return;
      }

      const unit = this.getSelectedValue(
        'adultWeightUnit',
        'kg'
      );

      this.breedEstimateValue.textContent =
        `~${this.formatWeight(
          estimate.typicalKg,
          unit
        )}`;

      if (
        this.state.selectedBreed &&
        this.state.selectedSex === 'female'
      ) {
        this.breedEstimateNote.textContent =
          'Typical female';
      } else if (
        this.state.selectedBreed &&
        this.state.selectedSex === 'male'
      ) {
        this.breedEstimateNote.textContent =
          'Typical male';
      } else if (
        this.state.selectedBreed &&
        estimate.femaleKg &&
        estimate.maleKg
      ) {
        this.breedEstimateNote.textContent =
          `Female about ${this.formatWeight(
            estimate.femaleKg,
            unit
          )} · Male about ${this.formatWeight(
            estimate.maleKg,
            unit
          )}`;
      } else {
        this.breedEstimateNote.textContent =
          'General expected adult-size estimate';
      }

      this.breedEstimate.hidden = false;
    }

    hideBreedEstimate() {
      if (this.breedEstimateValue) {
        this.breedEstimateValue.textContent = '';
      }

      if (this.breedEstimateNote) {
        this.breedEstimateNote.textContent = '';
      }

      if (this.breedEstimate) {
        this.breedEstimate.hidden = true;
      }
    }

    renderBreedNote() {
      const breed = this.state.selectedBreed;

      if (!breed) {
        this.hideBreedNote();
        return;
      }

      const confidence =
        breed.dataQuality?.confidence ||
        'medium';

      let message;

      if (confidence === 'high') {
        message =
          'This is a general breed estimate. Individual dogs and cats can still fall outside the reference range.';
      } else if (confidence === 'low') {
        message =
          'This is a broad breed estimate. Edit the expected adult weight if your breeder or vet has provided a better figure.';
      } else {
        message =
          'This is an approximate breed estimate. Edit the expected adult weight if you know a more accurate figure.';
      }

      if (
        this.state.selectedSex &&
        breed.dataQuality?.sexSpecificValuesAreEstimated
      ) {
        message +=
          ' The female and male values are estimated refinements.';
      }

      this.showBreedNote(
        message,
        confidence
      );
    }

    showBreedNote(
      text,
      confidence = 'medium'
    ) {
      if (!this.breedNote) {
        return;
      }

      this.breedNote.textContent = text;
      this.breedNote.dataset.confidence = confidence;
      this.breedNote.hidden = false;
    }

    hideBreedNote() {
      if (!this.breedNote) {
        return;
      }

      this.breedNote.textContent = '';

      this.breedNote.removeAttribute(
        'data-confidence'
      );

      this.breedNote.hidden = true;
    }

    onWeightUnitChange(event) {
      const nextUnit = String(
        event.target?.value || ''
      );

      const previousUnit =
        this.state.previousWeightUnit;

      if (
        nextUnit !== 'kg' &&
        nextUnit !== 'lb'
      ) {
        return;
      }

      this.convertWeightInput(
        this.weightInput,
        previousUnit,
        nextUnit
      );

      this.state.previousWeightUnit = nextUnit;
      this.recalculateIfReady();
    }

    onAdultWeightUnitChange(event) {
      const nextUnit = String(
        event.target?.value || ''
      );

      const previousUnit =
        this.state.previousAdultWeightUnit;

      if (
        nextUnit !== 'kg' &&
        nextUnit !== 'lb'
      ) {
        return;
      }

      this.convertWeightInput(
        this.adultWeightInput,
        previousUnit,
        nextUnit
      );

      this.state.previousAdultWeightUnit =
        nextUnit;

      if (
        this.state.selectedBreed ||
        this.state.selectedSizeWeightKg
      ) {
        this.renderBreedEstimate();
      }

      this.recalculateIfReady();
    }

    convertWeightInput(
      input,
      fromUnit,
      toUnit
    ) {
      if (
        !input ||
        !input.value ||
        !fromUnit ||
        !toUnit ||
        fromUnit === toUnit
      ) {
        return;
      }

      const value = Number(input.value);

      if (
        !Number.isFinite(value) ||
        value <= 0
      ) {
        return;
      }

      const kilograms =
        fromUnit === 'lb'
          ? value * KG_PER_LB
          : value;

      const convertedValue =
        toUnit === 'lb'
          ? kilograms / KG_PER_LB
          : kilograms;

      if (input === this.adultWeightInput) {
        this.setAdultWeightInputValue(
          this.formatInputNumber(convertedValue)
        );
      } else {
        input.value =
          this.formatInputNumber(convertedValue);
      }
    }

    recalculateIfReady() {
      if (!this.state.calculated) {
        return;
      }

      this.calculate({
        shouldScroll: false,
        showErrors: false
      });
    }

    onSubmit(event) {
      event.preventDefault();

      this.calculate({
        shouldScroll: true,
        showErrors: true
      });
    }

    calculate({
      shouldScroll = false,
      showErrors = true
    } = {}) {
      this.clearErrors();

      const parsed = this.parseInputs();

      if (!parsed.ok) {
        if (showErrors) {
          this.renderErrors(parsed.errors);
        }

        return;
      }

      const data = parsed.data;
      const stage = this.getStage(data.ageDays);

      this.renderWhoLine(data);

      if (stage === 'milk') {
        this.renderMilkStage(data);
      } else {
        const result = this.calculateSolidFood(
          data,
          stage
        );

        this.renderSolidResult(result);
      }

      this.showResults();

      if (shouldScroll) {
        requestAnimationFrame(() => {
          this.results?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      }
    }

    parseInputs() {
      const errors = [];

      const species = this.state.species;

      const weight = Number(
        this.weightInput?.value || 0
      );

      const age = Number(
        this.ageInput?.value || 0
      );

      const weightUnit = this.getSelectedValue(
        'weightUnit',
        'kg'
      );

      const ageUnit = this.getSelectedValue(
        'ageUnit',
        'months'
      );

      const adultWeightUnit = this.getSelectedValue(
        'adultWeightUnit',
        'kg'
      );

      const foodUnit = this.getSelectedValue(
        'foodUnit',
        'cup'
      );

      if (
        !Number.isFinite(weight) ||
        weight <= 0
      ) {
        errors.push(
          "Enter your puppy or kitten's current weight."
        );
      }

      if (
        !Number.isFinite(age) ||
        age <= 0
      ) {
        errors.push(
          "Enter your puppy or kitten's age."
        );
      }

      const weightKg = this.toKg(
        weight,
        weightUnit
      );

      const ageDays = this.toAgeDays(
        age,
        ageUnit
      );

      if (
        Number.isFinite(weightKg) &&
        weightKg > 90
      ) {
        errors.push(
          'That weight looks unusually high for a puppy or kitten. Please double-check it.'
        );
      }

      if (
        Number.isFinite(ageDays) &&
        ageDays > 900
      ) {
        errors.push(
          'That age looks unusually high. Check the number and selected age unit.'
        );
      }

      const enteredAdultWeight = Number(
        this.adultWeightInput?.value || 0
      );

      let adultWeightKg =
        enteredAdultWeight > 0
          ? this.toKg(
              enteredAdultWeight,
              adultWeightUnit
            )
          : null;

      if (
        !adultWeightKg &&
        species === 'kitten' &&
        ageDays >= 28
      ) {
        adultWeightKg = 4.5;
      }

      if (
        adultWeightKg &&
        weightKg &&
        adultWeightKg < weightKg
      ) {
        errors.push(
          'Expected adult weight should not be lower than the current weight.'
        );
      }

      if (
        adultWeightKg &&
        adultWeightKg > 120
      ) {
        errors.push(
          'That expected adult weight looks unusually high. Please double-check it.'
        );
      }

      const foodCalories = Number(
        this.foodCaloriesInput?.value || 0
      );

      if (foodCalories < 0) {
        errors.push(
          'Food calories cannot be negative.'
        );
      }

      if (
        foodCalories > 0 &&
        foodUnit === 'cup' &&
        foodCalories < 50
      ) {
        errors.push(
          'The kcal-per-cup value looks unusually low. Please check the food label.'
        );
      }

      if (
        foodCalories > 0 &&
        foodUnit === 'cup' &&
        foodCalories > 1000
      ) {
        errors.push(
          'The kcal-per-cup value looks unusually high. Please check the food label.'
        );
      }

      if (
        foodCalories > 0 &&
        foodUnit === 'kg' &&
        foodCalories < 500
      ) {
        errors.push(
          'The kcal-per-kg value looks unusually low. Please check the food label.'
        );
      }

      if (
        foodCalories > 0 &&
        foodUnit === 'kg' &&
        foodCalories > 10000
      ) {
        errors.push(
          'The kcal-per-kg value looks unusually high. Please check the food label.'
        );
      }

      if (errors.length) {
        return {
          ok: false,
          errors
        };
      }

      return {
        ok: true,
        data: {
          species,
          weight,
          weightKg,
          weightUnit,
          age,
          ageUnit,
          ageDays,
          ageMonths:
            ageDays / DAYS_PER_MONTH,
          adultWeightKg,
          adultWeightUnit,
          foodCalories,
          foodUnit,
          selectedBreed:
            this.state.selectedBreed,
          selectedSex:
            this.state.selectedSex,
          selectedSizeWeightKg:
            this.state.selectedSizeWeightKg,
          adultWeightSource:
            this.getAdultWeightSource(
              adultWeightKg
            )
        }
      };
    }

    getAdultWeightSource(adultWeightKg) {
      if (!adultWeightKg) {
        return 'none';
      }

      if (
        this.state.selectedBreed &&
        this.state.adultWeightWasAutoFilled
      ) {
        return this.state.selectedSex
          ? 'breed_and_sex'
          : 'breed';
      }

      if (
        this.state.selectedSizeWeightKg &&
        this.state.adultWeightWasAutoFilled
      ) {
        return 'size';
      }

      if (
        this.state.species === 'kitten' &&
        !this.adultWeightInput?.value
      ) {
        return 'average_cat';
      }

      return 'manual';
    }

    calculateSolidFood(data, stage) {
      const hasAdultWeight =
        Number.isFinite(data.adultWeightKg) &&
        data.adultWeightKg >= data.weightKg;

      const percentageAdult = hasAdultWeight
        ? Math.min(
            150,
            (
              data.weightKg /
              data.adultWeightKg
            ) * 100
          )
        : null;

      const factor = hasAdultWeight
        ? this.growthFactorByProgress(
            data.species,
            percentageAdult,
            data.ageMonths
          )
        : this.growthFactorByAge(
            data.species,
            data.ageMonths
          );

      const restingEnergy =
        70 * Math.pow(
          data.weightKg,
          0.75
        );

      const calories =
        restingEnergy * factor;

      const roundedCalories =
        Math.round(calories);

      const defaultFoodCalories =
        FOOD_DEFAULTS[data.species][
          data.foodUnit
        ];

      const foodCalories =
        data.foodCalories > 0
          ? data.foodCalories
          : defaultFoodCalories;

      const usingDefault =
        !(data.foodCalories > 0);

      const mealsPerDay =
        this.getMealsPerDay(
          data.species,
          data.ageMonths
        );

      const times =
        this.getMealTimes(mealsPerDay);

      let dailyAmount;
      let perMealAmount;
      let amountUnit;

      if (data.foodUnit === 'cup') {
        dailyAmount =
          calories / foodCalories;

        perMealAmount =
          dailyAmount / mealsPerDay;

        amountUnit = 'cups';
      } else {
        dailyAmount =
          calories /
          (foodCalories / 1000);

        perMealAmount =
          dailyAmount / mealsPerDay;

        amountUnit = 'grams';
      }

      return {
        ...data,
        stage,
        hasAdultWeight,
        percentageAdult,
        factor,
        restingEnergy,
        calories,
        roundedCalories,
        foodCalories,
        usingDefault,
        mealsPerDay,
        times,
        dailyAmount,
        perMealAmount,
        amountUnit,
        bigBreed:
          this.isLargeGrowingPet(data),
        atAdultSize:
          percentageAdult !== null &&
          percentageAdult >= 100
      };
    }

    growthFactorByProgress(
      species,
      percentageAdult,
      ageMonths
    ) {
      const progress = Math.max(
        0,
        Math.min(
          100,
          Number(percentageAdult) || 0
        )
      );

      if (species === 'puppy') {
        if (progress < 50) {
          return this.interpolate(
            3,
            2.5,
            progress / 50
          );
        }

        if (progress < 80) {
          return this.interpolate(
            2.5,
            2,
            (progress - 50) / 30
          );
        }

        return this.interpolate(
          2,
          1.6,
          (progress - 80) / 20
        );
      }

      if (progress < 50) {
        return this.interpolate(
          2.5,
          2.2,
          progress / 50
        );
      }

      if (progress < 80) {
        return this.interpolate(
          2.2,
          2,
          (progress - 50) / 30
        );
      }

      return this.interpolate(
        2,
        1.3,
        (progress - 80) / 20
      );
    }

    growthFactorByAge(
      species,
      ageMonths
    ) {
      if (species === 'puppy') {
        if (ageMonths < 4) {
          return 3;
        }

        if (ageMonths < 6) {
          return 2.5;
        }

        if (ageMonths < 12) {
          return 2;
        }

        if (ageMonths < 18) {
          return 1.8;
        }

        return 1.6;
      }

      if (ageMonths < 4) {
        return 2.5;
      }

      if (ageMonths < 8) {
        return 2.2;
      }

      if (ageMonths < 12) {
        return 2;
      }

      return 1.3;
    }

    interpolate(start, end, progress) {
      const safeProgress = Math.max(
        0,
        Math.min(
          1,
          Number(progress) || 0
        )
      );

      return (
        start +
        (end - start) * safeProgress
      );
    }

    getStage(ageDays) {
      if (ageDays < 28) {
        return 'milk';
      }

      if (ageDays < 56) {
        return 'weaning';
      }

      return 'weaned';
    }

    getMealsPerDay(
      species,
      ageMonths
    ) {
      if (ageMonths < 3) {
        return 4;
      }

      if (ageMonths < 6) {
        return 3;
      }

      if (
        species === 'kitten' &&
        ageMonths < 12
      ) {
        return 3;
      }

      return 2;
    }

    getMealTimes(meals) {
      if (meals >= 4) {
        return [
          '7:00am',
          '12:00pm',
          '5:00pm',
          '9:00pm'
        ];
      }

      if (meals === 3) {
        return [
          '7:00am',
          '1:00pm',
          '7:00pm'
        ];
      }

      return [
        '7:00am',
        '6:00pm'
      ];
    }

    isLargeGrowingPet(data) {
      if (
        !Number.isFinite(
          data.adultWeightKg
        )
      ) {
        return false;
      }

      if (data.species === 'puppy') {
        return data.adultWeightKg >= 25;
      }

      return data.adultWeightKg >= 6;
    }

    renderWhoLine(data) {
      if (!this.whoLine) {
        return;
      }

      const speciesLabel =
        data.species === 'puppy'
          ? 'Puppy'
          : 'Kitten';

      const breedLabel =
        data.selectedBreed?.name ||
        speciesLabel;

      const sexLabel =
        data.selectedSex === 'male'
          ? 'male'
          : data.selectedSex === 'female'
            ? 'female'
            : '';

      const identity = [
        breedLabel,
        sexLabel
      ]
        .filter(Boolean)
        .join(' · ');

      this.whoLine.textContent =
        `${identity} · ${this.formatDecimal(
          data.weight,
          1
        )} ${data.weightUnit} · ${this.formatDecimal(
          data.age,
          1
        )} ${data.ageUnit}`;
    }

    renderSolidResult(result) {
      if (this.solidResults) {
        this.solidResults.hidden = false;
      }

      if (this.milkStage) {
        this.milkStage.hidden = true;
        this.milkStage.innerHTML = '';
      }

      if (this.kcalValue) {
        this.kcalValue.textContent =
          new Intl.NumberFormat().format(
            result.roundedCalories
          );
      }

      this.renderBowl(result);
      this.renderMeals(result);
      this.renderWeaningBanner(result.stage);
      this.renderLargePetWarning(result);
      this.renderTrajectory(result);
      this.renderCtaContent(result.species);
    }

    renderBowl(result) {
      if (
        !this.bowlAmount ||
        !this.bowlCaption
      ) {
        return;
      }

      if (result.amountUnit === 'cups') {
        const dailyCups =
          this.roundTo(
            result.dailyAmount,
            0.05
          );

        const perMealCups =
          this.roundTo(
            result.perMealAmount,
            0.05
          );

        this.bowlAmount.innerHTML =
          `${this.escapeHtml(
            this.formatDecimal(
              dailyCups,
              2
            )
          )} <small>cups a day</small>`;

        this.bowlCaption.textContent =
          `About ${this.formatDecimal(
            perMealCups,
            2
          )} cup${perMealCups === 1 ? '' : 's'} per meal` +
          (
            result.usingDefault
              ? " · based on a typical food. Enter the food label's kcal/cup value for a more accurate amount."
              : ''
          );

        return;
      }

      const dailyGrams = Math.round(
        this.roundTo(
          result.dailyAmount,
          5
        )
      );

      const perMealGrams = Math.round(
        this.roundTo(
          result.perMealAmount,
          5
        )
      );

      this.bowlAmount.innerHTML =
        `${dailyGrams} <small>grams a day</small>`;

      this.bowlCaption.textContent =
        `About ${perMealGrams} g per meal` +
        (
          result.usingDefault
            ? " · based on a typical food. Enter the food label's kcal/kg value for a more accurate amount."
            : ''
        );
    }

    renderMeals(result) {
      if (
        !this.mealsWrap ||
        !this.mealReason
      ) {
        return;
      }

      this.mealsWrap.innerHTML =
        result.times
          .map(
            (time, index) => `
              <div class="feeding-calculator__meal-card">
                <strong>Meal ${index + 1}</strong>
                <span>${this.escapeHtml(time)}</span>
              </div>
            `
          )
          .join('');

      if (
        result.species === 'kitten' &&
        result.mealsPerDay === 3
      ) {
        this.mealReason.textContent =
          'Three meals: bigger stomach now, still growing fast, still needs steady fuel through the day.';

        return;
      }

      const reasons = {
        4:
          'Four small meals suit very young animals with small stomachs and help avoid long gaps between feeds.',
        3:
          'Three meals: bigger stomach now, still growing fast, still needs steady fuel through the day.',
        2:
          'Two meals usually suit older puppies and kittens as growth slows and meal capacity increases.'
      };

      this.mealReason.textContent =
        reasons[result.mealsPerDay] || '';
    }

    renderWeaningBanner(stage) {
      if (!this.weaningBanner) {
        return;
      }

      if (stage === 'weaning') {
        this.weaningBanner.innerHTML =
          '<strong>Weaning stage.</strong> Continue offering milk while gradually introducing food softened to a porridge-like texture. The solid-food amount below is a target to build towards.';

        this.weaningBanner.hidden = false;
        return;
      }

      this.weaningBanner.innerHTML = '';
      this.weaningBanner.hidden = true;
    }

    renderLargePetWarning(result) {
      if (
        !this.largeBreedWarning ||
        !this.largeBreedHeading ||
        !this.largeBreedText
      ) {
        return;
      }

      if (!result.bigBreed) {
        this.largeBreedWarning.hidden = true;
        this.largeBreedHeading.textContent = '';
        this.largeBreedText.textContent = '';
        return;
      }

      const breedName =
        result.selectedBreed?.name || '';

      if (result.species === 'puppy') {
        this.largeBreedHeading.textContent =
          breedName
            ? `${breedName}: grow this puppy slowly`
            : 'Large or giant breed: grow this puppy slowly';

        this.largeBreedText.textContent =
          'Feed a complete diet labelled for large-breed puppies and keep them lean. Do not add calcium or extra supplements unless your vet specifically recommends them. Overfeeding and growing too quickly increase joint risk.';
      } else {
        this.largeBreedHeading.textContent =
          breedName
            ? `${breedName}: allow steady growth`
            : 'Large cat breed: allow steady growth';

        this.largeBreedText.textContent =
          'Larger cat breeds may continue growing for longer than average. Maintain a lean body condition and continue a complete growth diet while your vet considers them to be growing.';
      }

      this.largeBreedWarning.hidden = false;
    }

    renderTrajectory(result) {
      if (!this.trajectory) {
        return;
      }

      if (result.atAdultSize) {
        this.trajectory.innerHTML =
          '<strong>Near adult size.</strong> Growth requirements may now be reducing. Review the feeding amount and transition timing with your vet.';

        return;
      }

      if (
        result.hasAdultWeight &&
        result.percentageAdult !== null
      ) {
        const roundedProgress = Math.round(
          result.percentageAdult
        );

        if (result.species === 'puppy') {
          this.trajectory.innerHTML =
            `<strong>About ${roundedProgress}% of expected adult weight.</strong> As your puppy passes about half, then 80%, of adult weight, the daily amount steps down. Re-check every 2 weeks or whenever the weight jumps.`;

          return;
        }

        this.trajectory.innerHTML =
          `<strong>About ${roundedProgress}% of expected adult weight.</strong> Recalculate every 2 weeks while your kitten is growing, then reduce the amount gradually as growth slows.`;

        return;
      }

      if (result.species === 'puppy') {
        this.trajectory.innerHTML =
          '<strong>Age-based estimate.</strong> Because expected adult weight was left blank, this result uses age and current weight. Recalculate every 2 weeks and use body condition to adjust the portion.';

        return;
      }

      this.trajectory.innerHTML =
        '<strong>Average-cat estimate.</strong> Because no breed or expected adult weight was selected, this uses an average adult cat weight of about 4.5 kg. Recalculate every 2 weeks while your kitten is growing.';
    }

    renderCtaContent(species) {
      if (this.ctaCopy) {
        this.ctaCopy.textContent =
          'Save this pet to ASK A VET™ and we will recalculate the amount as they grow and remind you when the meal schedule should change, so you never guess.';
      }

      if (this.enrichment) {
        this.enrichment.innerHTML =
          species === 'puppy'
            ? 'For fast eaters, a <strong>WOOPF™</strong> snuffle mat or lick mat can slow meals and add enrichment.'
            : 'A <strong>PURRZ™</strong> puzzle feeder can encourage natural hunting behaviour and slow meals.';
      }
    }

    renderMilkStage(data) {
      if (this.solidResults) {
        this.solidResults.hidden = true;
      }

      if (this.weaningBanner) {
        this.weaningBanner.hidden = true;
        this.weaningBanner.innerHTML = '';
      }

      if (this.largeBreedWarning) {
        this.largeBreedWarning.hidden = true;
      }

      const frequency =
        this.getNeonatalFrequency(
          data.ageDays
        );

      const animal =
        data.species === 'puppy'
          ? 'puppy'
          : 'kitten';

      const replacer =
        data.species === 'puppy'
          ? 'puppy milk replacer'
          : 'kitten milk replacer';

      if (this.milkStage) {
        this.milkStage.innerHTML = `
          <span class="feeding-calculator__milk-stage-title">
            Milk stage
          </span>

          <p class="feeding-calculator__milk-stage-lead">
            At <strong>${Math.round(
              data.ageDays
            )} days old</strong>, your ${animal} should receive all or almost all nutrition from milk rather than solid food.
          </p>

          <div class="feeding-calculator__schedule">
            <h3>How often to feed</h3>

            <div class="feeding-calculator__meal-grid">
              <div class="feeding-calculator__meal-card">
                <strong>${this.escapeHtml(
                  frequency.frequency
                )}</strong>

                <span>${this.escapeHtml(
                  frequency.perDay
                )}</span>
              </div>
            </div>
          </div>

          <ul>
            <li>
              <strong>If mum is present:</strong>
              allow nursing on demand and weigh the ${animal} daily.
            </li>

            <li>
              <strong>If hand-rearing:</strong>
              use a proper ${replacer}, not cow's milk, and follow the product's instructions for body weight.
            </li>

            <li>
              <strong>Keep them warm.</strong>
              A cold newborn may stop feeding and should be warmed carefully before a feed.
            </li>

            <li>
              <strong>Weaning generally begins around 3 to 4 weeks</strong>
              by gradually introducing softened food.
            </li>
          </ul>

          <div class="feeding-calculator__milk-stage-warning">
            If your ${animal} is not gaining weight, feeds poorly, feels cold,
            appears weak or limp, or cries continuously, contact a vet immediately.
          </div>
        `;

        this.milkStage.hidden = false;
      }

      if (this.ctaCopy) {
        this.ctaCopy.textContent =
          `Save your ${animal} to ASK A VET™ and track daily weight gain, feeding and weaning progress.`;
      }

      if (this.enrichment) {
        this.enrichment.innerHTML = '';
      }
    }

    getNeonatalFrequency(ageDays) {
      if (ageDays <= 7) {
        return {
          frequency: 'Every 2 to 3 hours',
          perDay: 'about 8 to 12 feeds a day'
        };
      }

      if (ageDays <= 14) {
        return {
          frequency: 'Every 3 to 4 hours',
          perDay: 'about 6 to 8 feeds a day'
        };
      }

      if (ageDays <= 21) {
        return {
          frequency: 'Every 4 to 5 hours',
          perDay: 'about 5 to 6 feeds a day'
        };
      }

      return {
        frequency: 'Every 5 to 6 hours',
        perDay:
          'about 4 to 5 feeds a day while beginning weaning'
      };
    }

    showResults() {
      if (!this.results) {
        return;
      }

      this.results.hidden = false;
      this.state.calculated = true;
    }

    hideResults() {
      if (!this.results) {
        return;
      }

      this.results.hidden = true;
      this.state.calculated = false;
    }

    restart() {
      this.hideResults();
      this.clearErrors();

      requestAnimationFrame(() => {
        this.form?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        this.weightInput?.focus({
          preventScroll: true
        });
      });
    }

    handleSaveButton() {
      if (!this.saveButton) {
        return;
      }

      this.saveButton.textContent =
        'Saved to your pet profile ✓';

      this.saveButton.style.background =
        '#d1fae5';

      this.saveButton.style.color =
        '#065f46';
    }

    onDocumentClick(event) {
      if (
        this.breedCombo &&
        !this.breedCombo.contains(event.target)
      ) {
        this.closeBreedPanel();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeBreedPanel();
      }
    }

    renderErrors(errors) {
      if (!this.errorsWrap) {
        return;
      }

      this.errorsWrap.innerHTML =
        errors
          .map(
            (error) =>
              `<div>${this.escapeHtml(
                error
              )}</div>`
          )
          .join('');

      this.errorsWrap.hidden = false;

      this.errorsWrap.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }

    clearErrors() {
      if (!this.errorsWrap) {
        return;
      }

      this.errorsWrap.innerHTML = '';
      this.errorsWrap.hidden = true;
    }

    toKg(value, unit) {
      const numericValue = Number(value);

      return unit === 'lb'
        ? numericValue * KG_PER_LB
        : numericValue;
    }

    toAgeDays(value, unit) {
      const numericValue = Number(value);

      if (unit === 'days') {
        return numericValue;
      }

      if (unit === 'weeks') {
        return numericValue * 7;
      }

      return (
        numericValue *
        DAYS_PER_MONTH
      );
    }

    toNullablePositiveNumber(value) {
      const numericValue = Number(value);

      return Number.isFinite(numericValue) &&
        numericValue > 0
        ? numericValue
        : null;
    }

    midpoint(first, second) {
      const firstNumber =
        this.toNullablePositiveNumber(first);

      const secondNumber =
        this.toNullablePositiveNumber(second);

      if (firstNumber && secondNumber) {
        return (
          (firstNumber + secondNumber) /
          2
        );
      }

      return firstNumber || secondNumber || null;
    }

    formatWeight(weightKg, unit) {
      if (unit === 'lb') {
        return `${this.formatDecimal(
          weightKg / KG_PER_LB,
          1
        )} lb`;
      }

      return `${this.formatDecimal(
        weightKg,
        1
      )} kg`;
    }

    formatInputNumber(value) {
      const numericValue = Number(value);

      if (
        !Number.isFinite(numericValue) ||
        numericValue <= 0
      ) {
        return '';
      }

      if (numericValue >= 100) {
        return String(
          Math.round(numericValue)
        );
      }

      return numericValue
        .toFixed(2)
        .replace(/\.?0+$/, '');
    }

    formatDecimal(value, digits = 2) {
      const numericValue = Number(value);

      if (
        !Number.isFinite(numericValue)
      ) {
        return '0';
      }

      return numericValue
        .toFixed(digits)
        .replace(/\.?0+$/, '');
    }

    roundTo(value, step) {
      return (
        Math.round(value / step) *
        step
      );
    }

    normaliseSearchText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[’']/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    slugify(value) {
      return this.normaliseSearchText(value)
        .replace(/\s+/g, '-');
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  if (
    !customElements.get(
      's-feeding-calculator'
    )
  ) {
    customElements.define(
      's-feeding-calculator',
      SFeedingCalculator
    );
  }
})();
